import { Button, Typography } from "@/components/atoms";
import { MailIcon, SubmitProofTileIcon } from "@/components/atoms/icons";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from "@/core/constants/dynatraceCatalog";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import React, { useCallback } from "react";
import { BackHandler, Linking, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

/**
 * Where the ADHA Pay Now flow was opened from. The unit-details price card is
 * the default; the home Reservation Summary sheet passes `home`.
 */
export type PaymentFlowReturnTo = "unitDetails" | "home";

export function SubmitPaymentProofScreen() {
  useCatalogDynatraceView(
    DT_SCREEN.PAYMENT_PROOF,
    'payment_proof_submit_viewed',
    true,
  );
  const colors = useUIColors();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { propertyId, method, returnTo } = useLocalSearchParams<{
    propertyId?: string;
    method?: "bankTransfer" | "proofOfMortgage";
    returnTo?: PaymentFlowReturnTo;
  }>();
  const returnsToHome = returnTo === "home";
  // Same instruction split as web's SubmissionReceivedDialog.
  const instructionKey =
    method === "proofOfMortgage"
      ? "propertyDetails.submitProofMortgageInstruction"
      : "propertyDetails.submitProofPaymentInstruction";

  // The submission is already recorded, so leaving this screen never goes back
  // into the payment flow (Complete Your Payment, Bank Transfer Details, ...).
  // It jumps back to the screen Pay Now was opened from, already on the stack.
  const handleBackToOrigin = useCallback(() => {
    if (returnsToHome) {
      router.dismissTo("/(auth)/(home)" as any);
      return;
    }
    router.dismissTo({
      pathname: "/(auth)/property-details",
      params: { propertyId },
    } as any);
  }, [returnsToHome, propertyId]);

  // Android hardware back does the same instead of popping one flow screen.
  useFocusEffect(
    useCallback(() => {
      const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
        handleBackToOrigin();
        return true;
      });
      return () => subscription.remove();
    }, [handleBackToOrigin]),
  );

  const handleOpenMail = () => {
    const email = t("propertyDetails.submitProofEmail").trim();
    if (!email) {
      return;
    }
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.TOP_UP,
      actionName: ALMETLAA_ACTION.TOP_UP.PAYMENT_PROOF_SUBMIT_CLICK,
      eventType: 'action',
    });
    trackCatalogEvent({
      screen: DT_SCREEN.PAYMENT_PROOF,
      actionName: 'payment_proof_submit',
      eventType: 'action',
    });
    Linking.openURL(`mailto:${email}`).catch((error) => {
      logger.error("SubmitPaymentProofScreen: mailto failed", error);
      trackCatalogEvent({
        screen: DT_SCREEN.PAYMENT_PROOF,
        actionName: 'payment_proof_submit_error',
        eventType: 'error',
      });
    });
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <TextHeader title={t("propertyDetails.submissionReceivedTitle")} onBackPress={handleBackToOrigin} />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingBottom: 24 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.iconCircle}>
          <SubmitProofTileIcon />
        </View>

        <Typography variant="title2" color="primary" style={styles.title}>
          {t("propertyDetails.submissionReceivedTitle")}
        </Typography>

        <Typography variant="body" color="secondary" style={styles.subtitle}>
          {t(instructionKey)}
        </Typography>

        <Pressable
          onPress={handleOpenMail}
          accessibilityRole="link"
          accessibilityLabel={t("propertyDetails.submitProofEmail")}
          style={[
            styles.emailBox,
            { backgroundColor: colors.backgroundLevel1, borderColor: UI_PRIMITIVES.yellowDark },
          ]}
        >
          <MailIcon size={18} color={colors.fontPrimary} />
          <Typography variant="body" color="primary" style={styles.emailText}>
            {t("propertyDetails.submitProofEmail")}
          </Typography>
        </Pressable>

        <Typography variant="body" color="tertiary" style={styles.reviewNote}>
          {t("propertyDetails.submitProofReviewNote")}
        </Typography>
      </ScrollView>

      <View style={[styles.footer, { paddingBottom: 16 + insets.bottom, backgroundColor: colors.backgroundLevel2 }]}>
        <Button
          title={returnsToHome ? t("payments.backToHome") : t("propertyDetails.backToUnitDetails")}
          variant="primary"
          fullWidth
          onPress={handleBackToOrigin}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingTop: 32,
    alignItems: "center",
    gap: 12,
  },
  iconCircle: {
    marginBottom: 4,
  },
  title: {
    fontWeight: "700",
    textAlign: "center",
  },
  subtitle: {
    textAlign: "center",
    marginTop: 14,
  },
  emailBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: "100%",
    maxWidth: 319,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginTop: 4,
    borderLeftWidth: 4
  },
  emailText: {
    flexShrink: 1,
    fontWeight: "600",
  },
  // Font/Family, Font/Size/Base Regular, weight 400, LH 22px, letter-spacing 0
  // (Typography's "body" variant gives Matter-Regular/16px; LH overridden to 22).
  reviewNote: {
    textAlign: "center",
    marginTop: 8,
    fontWeight: "400",
    lineHeight: 22,
    letterSpacing: 0,
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 12,
  },
});

export default SubmitPaymentProofScreen;
